import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PartialUserPosition } from '@app/onboarding/onboarding-api.model';
import { LocalizationService } from '@app/shared/localization.service';

@Injectable({
  providedIn: 'root',
})
export class ExperienceDateService {
  public localeId = 'en-US';

  constructor(
    private translateService: TranslateService,
    private localizationService: LocalizationService
  ) {
    this.localeId = this.localizationService.userLocale;
  }

  /**
   * Sets a label of the date duration of the experience and along with the months and years
   *
   * @param experience - the experience object.
   */
  public buildDate(experience: PartialUserPosition): string {
    let { startDate, endDate } = experience;
    const isCurrent: boolean = !endDate;
    let startDateNew: Date = new Date(startDate);
    let endDateNew: Date = new Date(endDate);

    const monthDiff: number = this.getMonthsDiff(
      startDateNew,
      isCurrent ? new Date() : endDateNew
    );
    const dateLabel: string = this.getDateDiffLabel(monthDiff);

    return this.getCompleteDate(startDateNew, endDateNew, dateLabel, isCurrent);
  }

  /**
   * Returns the number of months between two dates
   *
   * @param startDate: date the user started the experience
   * @param endDate: date the user ended the experience
   */
  private getMonthsDiff(startDate: Date, endDate: Date): number {
    const date1: Date = new Date(startDate);
    const date2: Date = new Date(endDate);
    const years: number = date2.getFullYear() - date1.getFullYear();
    return years * 12 + (date2.getMonth() - date1.getMonth());
  }

  /**
   * Gets the label of the duration of the experience
   *
   * @param monthsDiff - the difference in months between the start and end date
   */
  private getDateDiffLabel(monthsDiff: number): string {
    let yearsDiff: number = 0;
    if (monthsDiff > 11) {
      yearsDiff = Math.floor(monthsDiff / 12);
      monthsDiff = monthsDiff - yearsDiff * 12;
    }

    const i18n = this.translateService.instant([
      'Core_Year',
      'Core_Month',
      'Onboarding_ExperienceDateYears',
      'Core_Months',
    ]);

    const yearsLabel: string = (
      yearsDiff === 1 ? i18n.Core_Year : i18n.Onboarding_ExperienceDateYears
    ).toLowerCase();
    const monthsLabel: string = (
      monthsDiff <= 1 ? i18n.Core_Month : i18n.Core_Months
    ).toLowerCase();

    // only month is set
    if (
      (isNaN(yearsDiff) || yearsDiff === 0) &&
      !isNaN(monthsDiff) &&
      monthsDiff > 0
    ) {
      return `${monthsDiff} ${monthsLabel}`;
    }
    // only year is set
    if (
      !isNaN(yearsDiff) &&
      yearsDiff > 0 &&
      (isNaN(monthsDiff) || monthsDiff === 0)
    ) {
      return `${yearsDiff} ${yearsLabel}`;
    }

    if (
      !isNaN(yearsDiff) &&
      yearsDiff > 0 &&
      !isNaN(monthsDiff) &&
      monthsDiff > 0
    ) {
      return `${yearsDiff} ${yearsLabel} ${monthsDiff} ${monthsLabel}`;
    }

    return `1 ${monthsLabel}`;
  }

  /**
   * Gets a formatted date of the experience in short form eg Jan 2000
   *
   * @param startDate: date the user started the experience
   * @param endDate: date the user ended the experience
   * @param dateDiffLabel: the calculated duration difference between the start and end date
   * @param isCurrent: if the experience is the current experience
   */
  private getCompleteDate(
    startDate: Date,
    endDate: Date,
    dateDiffLabel: string,
    isCurrent: boolean
  ): string {
    const formattedStartDate = `${startDate.toLocaleString(this.localeId, {
      month: 'short',
    })} ${startDate.getFullYear()}`;
    const formattedEndDate = `${endDate.toLocaleString(this.localeId, {
      month: 'short',
    })} ${endDate.getFullYear()}`;

    let formattedDate = `${formattedStartDate} - ${formattedEndDate} (${dateDiffLabel})`;

    if (isCurrent) {
      formattedDate = `${formattedStartDate} - ${this.translateService.instant(
        'Onboarding_ExperienceDatePresent'
      )} (${dateDiffLabel})`;
    }

    return formattedDate;
  }
}
